import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import colors from '../../../themes/main-colors';
import { toCurrencyDisplayFormat } from '../../../util/format';
import { ViewBillDetails } from '../../../__generated__/pge-types';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Box, ClickAwayListener, Tooltip } from '@material-ui/core';
import isNil from 'lodash/isNil';
import { TpaAccountDetails } from '../../need-more-time-to-pay/tpa/useTPA';

import TextLink from '../../text-link';
import { useAccountCustomerDashboard } from '../../../hooks/useAccountCustomerDashboard';

export interface CurrentBillCardBillSummaryProps {
  viewBillDetails?: ViewBillDetails | null;
  isMobile?: boolean;
  tpaDetails?: TpaAccountDetails | undefined;
  isNewAccount?: boolean | null | undefined;
}

const useTooltipStyles = makeStyles((theme: Theme) =>
  createStyles({
    tooltip: {
      backgroundColor: '#fff',
      color: colors.sparkBlue,
      maxWidth: 260,
      height: 'auto',
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
      padding: '1em',
    },
  }),
);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
    },
    colorTextPrimary: {
      color: colors.noirBlur,
    },
    containerRow: {
      margin: theme.spacing(2, 0, 2, 0),
    },
    mobileContainerRow: {
      margin: theme.spacing(0, 0, 2, 0),
    },
    boxContainer: {
      backgroundColor: colors.gray93,
      padding: '1em',
    },
    money: {
      fontWeight: 'bold',
      margin: theme.spacing(1, 0),
      minWidth: '100px',
      textAlign: 'right',
    },
    boldText: {
      fontWeight: 'bold',
    },
    closeIcon: {
      position: 'absolute',
      top: '4px',
      right: '-10px',
    },
    tooltip: {
      fontWeight: 'bold',
      fontSize: theme.typography.pxToRem(20),
      cursor: 'pointer',
      textDecoration: 'none',
      borderBottom: 'dashed',
      borderWidth: '1px !important',
      '&:hover': {
        borderBottom: `dashed ${theme.palette.secondary.main}`,
      },
      '&:active': {
        color: theme.palette.primary.dark,
        borderBottom: `dashed ${theme.palette.primary.dark}`,
      },
    },
  }),
);
const CurrentBillCardBillSummary: FunctionComponent<CurrentBillCardBillSummaryProps> = ({
  viewBillDetails,
  isMobile,
  tpaDetails,
  isNewAccount,
}) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { showLegacyUI } = useAccountCustomerDashboard();
  const tooltipClasses = useTooltipStyles();
  const [isOpen, toggleOpen] = useState(false);

  return (
    <>
      <Grid container direction="column">
        <Grid item direction="column">
          {!isNewAccount && showLegacyUI && (
            <>
              <Grid
                item
                className={
                  !isMobile ? classes.containerRow : classes.mobileContainerRow
                }
              >
                <Typography variant="body1">
                  {t('BILL_DETAILS_TEXT')}
                </Typography>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
            </>
          )}
          <Grid item className={classes.containerRow}>
            <Box
              display="flex"
              justifyContent={'space-between'}
              alignItems="center"
            >
              <Typography className={classes.boldText} variant="body1">
                {t('BALANCES_FORWARD')}
              </Typography>
              <Typography className={classes.money} variant="body1">
                {toCurrencyDisplayFormat(
                  viewBillDetails?.totalBalanceAfterBill || 0,
                  true,
                  'CR',
                )}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent={'space-between'}
              alignItems="center"
              width={'100%'}
            >
              <Typography
                style={{ marginLeft: '1em' }}
                className={classes.colorTextPrimary}
                variant="body1"
              >
                {t('PREVIOUS_BALANCE')}
              </Typography>
              <Typography className={classes.money} variant="body1">
                {toCurrencyDisplayFormat(
                  viewBillDetails?.previousBalance || 0,
                  true,
                  'CR',
                )}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent={'space-between'}
              alignItems="center"
            >
              <Typography
                className={classes.colorTextPrimary}
                style={{ marginLeft: '1em' }}
                variant="body1"
              >
                {t('PAYMENTS_ADJUSTMENTS')}
              </Typography>
              <ClickAwayListener onClickAway={() => toggleOpen(false)}>
                <Tooltip
                  open={isOpen}
                  classes={tooltipClasses}
                  onClose={() => toggleOpen(false)}
                  title={
                    <>
                      <Typography component={'div'}>
                        {richT('PAYMENT_ADJUSTMENT_TOOLTIP')}
                      </Typography>
                    </>
                  }
                  placement={'bottom-start'}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                >
                  <span>
                    <TextLink
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                      tabIndex={-1}
                      onClick={e => {
                        e.preventDefault();
                        toggleOpen(!isOpen);
                      }}
                      external
                      tooltip
                    >
                      {toCurrencyDisplayFormat(
                        viewBillDetails?.totalAdjustments || 0,
                        true,
                        'CR',
                      )}
                    </TextLink>
                  </span>
                </Tooltip>
              </ClickAwayListener>
            </Box>
            <Box
              display="flex"
              justifyContent={'space-between'}
              alignItems="center"
            >
              <Typography className={classes.boldText} variant="body1">
                {t('CURRENT_CHARGES')}
              </Typography>
              <Typography className={classes.money} variant="body1">
                {toCurrencyDisplayFormat(
                  viewBillDetails?.totalCurrentCharges || 0,
                  true,
                  'CR',
                )}
              </Typography>
            </Box>
            {tpaDetails?.isEnrolled &&
              !isNil(
                tpaDetails?.enrolledInstallmentDetails?.currentTpaMonthBalance,
              ) && (
                <Box
                  display="flex"
                  justifyContent={'space-between'}
                  alignItems="center"
                >
                  <Typography
                    className={classes.boldText}
                    variant="body1"
                    data-testid="payment-plan-amount"
                  >
                    {t('PAYMENT_PLAN_INSTALLMENT')}
                  </Typography>
                  <Typography
                    className={classes.money}
                    data-testid="payment-plan-amount-value"
                    variant="body1"
                  >
                    {toCurrencyDisplayFormat(
                      tpaDetails?.enrolledInstallmentDetails
                        ?.currentTpaMonthBalance || 0,
                      true,
                      'CR',
                    )}
                  </Typography>
                </Box>
              )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Box
              display="flex"
              justifyContent={'space-between'}
              alignItems="center"
            >
              <Typography className={classes.boldText}>
                {t('TOTAL_CHARGES')}
              </Typography>
              <Typography className={classes.money}>
                {toCurrencyDisplayFormat(
                  viewBillDetails?.amountDue || 0,
                  true,
                  'CR',
                )}
              </Typography>
            </Box>
            {!isNewAccount && !showLegacyUI && (
              <>
                <Grid item>
                  <Divider />
                </Grid>
                <Grid
                  item
                  className={
                    !isMobile
                      ? classes.containerRow
                      : classes.mobileContainerRow
                  }
                >
                  <Typography variant="body1">
                    {t('BILL_DETAILS_TEXT')}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default CurrentBillCardBillSummary;
